import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { getAllFlujosEntradas } from '../../services/apis';
import { getCampaignRangeByStartYear, getCampaignStartYearFromDate } from '../../utils/dashboardData';
import { normalizeFlujosEntradas } from '../../utils/flujosEntradasMapper';
import { downloadDailyGlobalBalancePdf } from '../../utils/globalBalancePdf';
import { downloadMassBalancePdf } from '../../utils/massBalancePdf';
import { downloadTotalLossesChartPdf } from '../../utils/totalLossesChartPdf';
import { buildDailyBalance } from '../../utils/varietyBalanceReport';
import { downloadDailyVarietyBalancePdf } from '../../utils/varietyBalancePdf';

const getErrorMessage = (error) => {
  const message = error?.response?.data?.message || error?.message;
  return Array.isArray(message) ? message.join(', ') : message || 'No se ha podido generar el informe.';
};

export const DailyBalanceReportAction = ({
  almacenes,
  cajasPalet,
  dateRange,
  disabled = false,
  mode,
  pesoPalotMerma,
  selectedVarietyId,
  selectedWarehouseIds,
  variedades,
  variedadesEntrada = variedades,
}) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const isVarietyReport = mode === 'variety';
  const isMassReport = mode === 'mass';
  const isLossesReport = mode === 'losses';
  const selectedVariety = variedades.find(
    (variedad) => String(variedad.id) === String(selectedVarietyId)
  );
  const selectedWarehouses = selectedWarehouseIds.length > 0
    ? almacenes.filter((almacen) => selectedWarehouseIds.includes(almacen.id))
    : almacenes;
  const title = isVarietyReport
    ? 'Balance diario por variedad'
    : isMassReport ? 'Balance de masas'
      : isLossesReport ? 'Grafico de mermas totales' : 'Balance diario global';
  const subject = isVarietyReport || isMassReport
    ? selectedVariety?.nombre || 'Sin variedad'
    : isLossesReport ? selectedVariety?.nombre || 'Todas las variedades' : 'Todas las variedades';

  const generateReport = async () => {
    setError('');

    if ((isVarietyReport || isMassReport) && !selectedVariety) {
      setError('Selecciona una variedad para generar el informe.');
      return;
    }

    if (!dateRange.desde || !dateRange.hasta || dateRange.desde > dateRange.hasta) {
      setError('El intervalo de fechas seleccionado no es valido.');
      return;
    }

    setGenerating(true);

    try {
      const queryStart = isVarietyReport
        ? getCampaignRangeByStartYear(getCampaignStartYearFromDate(dateRange.desde)).startValue
        : dateRange.desde;
      const response = await getAllFlujosEntradas({
        desde: queryStart,
        hasta: dateRange.hasta,
        codigo_variedad: isVarietyReport || isMassReport || (isLossesReport && selectedVariety)
          ? selectedVariety.id
          : undefined,
        ids_almacenes: selectedWarehouseIds.length > 0 ? selectedWarehouseIds.join(',') : undefined,
        sortBy: 'fecha',
        sortDirection: 'asc',
        limit: 500,
      });
      const entries = normalizeFlujosEntradas(response, {
        almacenes,
        variedades: variedadesEntrada,
        cajasPalet,
      });
      const days = buildDailyBalance(entries, {
        cajasPalet,
        pesoPalotMerma,
        visibleRange: isVarietyReport ? dateRange : undefined,
      });

      if (days.length === 0) {
        setError('No hay entradas para los filtros seleccionados.');
        return;
      }

      const pdfOptions = {
        days,
        warehouseLabel: selectedWarehouses.map((almacen) => almacen.nombre).join(' / ') || 'Todos',
        desde: dateRange.desde,
        hasta: dateRange.hasta,
      };

      if (isMassReport) {
        await downloadMassBalancePdf({
          ...pdfOptions,
          totals: days[days.length - 1].accumulated,
          varietyName: selectedVariety.nombre,
        });
      } else if (isLossesReport) {
        await downloadTotalLossesChartPdf({
          ...pdfOptions,
          varietyId: selectedVariety?.id,
          varietyLabel: selectedVariety?.nombre || 'Todas las variedades',
        });
      } else if (isVarietyReport) {
        await downloadDailyVarietyBalancePdf({ ...pdfOptions, varietyName: selectedVariety.nombre });
      } else {
        await downloadDailyGlobalBalancePdf(pdfOptions);
      }
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <Divider sx={{ mt: 2.5 }} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ pt: 2.25, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', minWidth: 0 }}>
          <FactCheckOutlinedIcon color="primary" />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800 }}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {subject} - {selectedWarehouses.length} almacen(es)
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={generating ? <CircularProgress size={17} color="inherit" /> : <DownloadRoundedIcon />}
          onClick={generateReport}
          disabled={disabled || generating}
          sx={{ minWidth: 154 }}
        >
          {generating ? 'Generando...' : 'Generar PDF'}
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </>
  );
};
